import { Checkbox } from "@components/common/checkbox"
import { Badge } from "@components/common/badge"
import { Task, ViewMode } from "gantt-task-react"
import { DictType } from "@pma/types"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@components/common/alert-dialog"
import useTranslator from "@hooks/use-translator"
import axios from "axios"
import { EDIT_TASK_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { Dispatch, SetStateAction, useState } from "react"
import { convertDateToTimestamp } from "@pma/utils/helpers"

type Props = {
  isChecked: boolean
  onViewListChange: (isChecked: boolean) => void
  onViewModeChange: (viewMode: ViewMode) => void
}

export const ViewSwitcher = ({ onViewModeChange, onViewListChange, isChecked }: Props) => {
  const [activeView, setActiveView] = useState("day")

  const { translate } = useTranslator()
  return (
    <div className='flex items-center gap-2 pb-4 pt-2'>
      <Badge
        className='flex w-[70px] cursor-pointer items-center justify-center'
        variant={activeView === "day" ? "default" : "outline"}
        onClick={() => {
          onViewModeChange(ViewMode.Day)
          setActiveView("day")
        }}
      >
        {translate("Day")}
      </Badge>
      <Badge
        className='flex w-[70px] cursor-pointer items-center justify-center'
        variant={activeView === "week" ? "default" : "outline"}
        onClick={() => {
          onViewModeChange(ViewMode.Week)
          setActiveView("week")
        }}
      >
        {translate("Week")}
      </Badge>
      <Badge
        className='flex w-[70px] cursor-pointer items-center justify-center'
        variant={activeView === "month" ? "default" : "outline"}
        onClick={() => {
          onViewModeChange(ViewMode.Month)
          setActiveView("month")
        }}
      >
        {translate("Month")}
      </Badge>

      <div className='flex items-center gap-2'>
        <Checkbox defaultChecked={isChecked} onClick={() => onViewListChange(!isChecked)} />
        <span className='text-sm'>{translate("Show task list")} </span>{" "}
      </div>
    </div>
  )
}

export function ConfirmDialog({
  isOpen,
  setIsOpen,
  task,
  setTasks,
  ganttTasks,
}: {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  task: DictType
  setTasks: Dispatch<SetStateAction<Task[] | null>>
  ganttTasks: Task[] | null
}) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const token = parseCookies()[ACCESS_TOKEN]

  const updateTaskTimeline = async () => {
    const response = await axios.post(
      SERVER_URL + EDIT_TASK_ENDPOINT,
      {
        task_id: Number(task.id),
        inform_users: true,
        start_at: convertDateToTimestamp(task.start as any),
        finish_to: convertDateToTimestamp(task.end as any),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )

    return response.data
  }
  const updateTaskTimelineMutation = useMutation({
    mutationKey: ["update-task-timeline", task],
    mutationFn: updateTaskTimeline,
    onSuccess: (data) => {
      if (ganttTasks) {
        const newTaskList = ganttTasks?.map((item: any) => {
          if (item.id == data.id) {
            return { ...item, start: new Date(data.start_at), end: new Date(data.finish_to) }
          }
          return item
        })
        setTasks(newTaskList)
      }
      toast({
        title: translate("Success"),
        description: translate("Task timeline has been updated"),
        variant: "success",
      })
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: translate("Unable to update the task timeline"),
        variant: "error",
      })
    },
  })

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Confirm")} {task?.name} {translate("timeline")}
          </AlertDialogTitle>
          <AlertDialogDescription className={isHebrew ? "rtl" : "ltr"}>
            {translate("Do you want to change the timeline for this tasks")}?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{translate("Cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={() => updateTaskTimelineMutation.mutate()}>
            {translate("Confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
